import type {
  AdminCommerceOrderDetail,
  AdminCommerceOrderListResult,
  CommerceOrderEvent,
  CommerceOrderStatus,
  SettleExternalCommerceOrderInput,
  TransitionCommerceOrderInput,
} from "@yahshi/contracts";
import { adminCommerceOrderListQuerySchema, canTransitionCommerceOrder, isExternalCommerceSettlement, updateCommerceOrderTitleUgInputSchema } from "@yahshi/contracts";
import { M1ApiError } from "../../m1-app/data/M1AppApi";
import type { CommerceOrderApi } from "./CommerceOrderApi";

const now = () => new Date().toISOString();

function event(orderId: string, type: CommerceOrderEvent["type"], toStatus: CommerceOrderStatus | null, extra: Partial<CommerceOrderEvent> = {}): CommerceOrderEvent {
  return {
    id: `cev_${orderId}_${type}_${toStatus ?? "none"}`,
    orderId,
    type,
    fromStatus: extra.fromStatus ?? null,
    toStatus,
    eventKey: extra.eventKey ?? null,
    channel: extra.channel ?? null,
    note: extra.note ?? "状态变更",
    createdAt: extra.createdAt ?? now(),
  };
}

const seedOrders: AdminCommerceOrderDetail[] = [
  {
    order: {
      id: "cord_demo_20260915001", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      userId: "11111111-1111-4111-8111-111111111111", userPublicId: "1001", userName: "林默",
      idempotencyKey: "idem_demo_created_001", amountMinor: 1990, currency: "CNY", title: "月度会员",
      status: "created", payload: { sku: "vip-month", source: "mini-program", level: "member" }, paymentOrderId: null, version: 1,
      createdAt: "2026-09-15T02:18:00.000Z", updatedAt: "2026-09-15T02:18:00.000Z",
      paidAt: null, completedAt: null, refundedAt: null, canceledAt: null,
    },
    events: [event("cord_demo_20260915001", "status", "created", { createdAt: "2026-09-15T02:18:00.000Z", note: "创建订单" })],
  },
  {
    order: {
      id: "cord_demo_20260915002", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      userId: "11111111-1111-4111-8111-111111111111", userPublicId: "1001", userName: "林默",
      idempotencyKey: "idem_demo_paid_002", amountMinor: 1990, currency: "CNY", title: "月度会员",
      status: "paid", payload: { sku: "vip-month" }, paymentOrderId: "ord_demo_20260910001", version: 2,
      createdAt: "2026-09-10T11:17:00.000Z", updatedAt: "2026-09-10T11:18:00.000Z",
      paidAt: "2026-09-10T11:18:00.000Z", completedAt: null, refundedAt: null, canceledAt: null,
    },
    events: [
      event("cord_demo_20260915002", "status", "created", { createdAt: "2026-09-10T11:17:00.000Z", note: "创建订单" }),
      event("cord_demo_20260915002", "status", "paid", { fromStatus: "created", createdAt: "2026-09-10T11:18:00.000Z" }),
      event("cord_demo_20260915002", "notification", "paid", { fromStatus: "paid", eventKey: "order.paid", channel: "in_app", note: "站内信已记录，未发送短信", createdAt: "2026-09-10T11:18:01.000Z" }),
    ],
  },
  {
    order: {
      id: "cord_demo_20260915003", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      userId: "55555555-5555-4555-8555-555555555555", userPublicId: "1005", userName: "程一",
      idempotencyKey: "idem_demo_completed_003", amountMinor: 12800, currency: "CNY", title: "年度会员",
      status: "completed", payload: { sku: "vip-year", entitlementDays: 365 }, paymentOrderId: null, version: 3,
      createdAt: "2026-09-08T05:20:00.000Z", updatedAt: "2026-09-08T06:12:00.000Z",
      paidAt: "2026-09-08T05:28:00.000Z", completedAt: "2026-09-08T06:12:00.000Z", refundedAt: null, canceledAt: null,
    },
    events: [
      event("cord_demo_20260915003", "status", "created", { createdAt: "2026-09-08T05:20:00.000Z", note: "创建订单" }),
      event("cord_demo_20260915003", "status", "paid", { fromStatus: "created", createdAt: "2026-09-08T05:28:00.000Z" }),
      event("cord_demo_20260915003", "notification", "paid", { fromStatus: "paid", eventKey: "order.paid", channel: "in_app", note: "站内信已记录，未发送短信", createdAt: "2026-09-08T05:28:01.000Z" }),
      event("cord_demo_20260915003", "status", "completed", { fromStatus: "paid", createdAt: "2026-09-08T06:12:00.000Z", note: "履约完成" }),
    ],
  },
  {
    order: {
      id: "cord_demo_20260909004", appId: "app_yahshi_admin", appName: "Yahshi Admin", appCode: "yahshi-admin",
      userId: "33333333-3333-4333-8333-333333333333", userPublicId: "1003", userName: "周野",
      idempotencyKey: "idem_demo_refunded_004", amountMinor: 600, currency: "CNY", title: "内容解锁",
      status: "refunded", payload: { contentId: "episode-12" }, paymentOrderId: null, version: 3,
      createdAt: "2026-09-07T08:20:00.000Z", updatedAt: "2026-09-09T03:40:00.000Z",
      paidAt: "2026-09-07T08:22:00.000Z", completedAt: null, refundedAt: "2026-09-09T03:40:00.000Z", canceledAt: null,
    },
    events: [
      event("cord_demo_20260909004", "status", "created", { createdAt: "2026-09-07T08:20:00.000Z", note: "创建订单" }),
      event("cord_demo_20260909004", "status", "paid", { fromStatus: "created", createdAt: "2026-09-07T08:22:00.000Z" }),
      event("cord_demo_20260909004", "notification", "paid", { fromStatus: "paid", eventKey: "order.paid", channel: "in_app", note: "站内信已记录，未发送短信", createdAt: "2026-09-07T08:22:01.000Z" }),
      event("cord_demo_20260909004", "status", "refunded", { fromStatus: "paid", createdAt: "2026-09-09T03:40:00.000Z" }),
      event("cord_demo_20260909004", "notification", "refunded", { fromStatus: "refunded", eventKey: "order.refunded", channel: "in_app", note: "站内信已记录，未发送短信", createdAt: "2026-09-09T03:40:01.000Z" }),
    ],
  },
  {
    order: {
      id: "cord_demo_20260908005", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      userId: "44444444-4444-4444-8444-444444444444", userPublicId: "1004", userName: "顾言",
      idempotencyKey: "idem_demo_canceled_005", amountMinor: 1, currency: "CNY", title: "联调订单",
      status: "canceled", payload: { env: "sandbox" }, paymentOrderId: null, version: 2,
      createdAt: "2026-09-06T04:12:00.000Z", updatedAt: "2026-09-06T04:30:00.000Z",
      paidAt: null, completedAt: null, refundedAt: null, canceledAt: "2026-09-06T04:30:00.000Z",
    },
    events: [
      event("cord_demo_20260908005", "status", "created", { createdAt: "2026-09-06T04:12:00.000Z", note: "创建订单" }),
      event("cord_demo_20260908005", "status", "canceled", { fromStatus: "created", createdAt: "2026-09-06T04:30:00.000Z", note: "用户取消" }),
    ],
  },
  {
    order: {
      id: "cord_demo_20260916006", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      userId: "11111111-1111-4111-8111-111111111111", userPublicId: "1001", userName: "林默",
      idempotencyKey: "idem_demo_virtual_006", amountMinor: 0, currency: "CNY", title: "旧版外部结清业务单",
      status: "created", payload: { settlement: "external", channel: "wechat_virtual", level: "member" }, paymentOrderId: null, version: 1,
      createdAt: "2026-09-16T03:12:00.000Z", updatedAt: "2026-09-16T03:12:00.000Z",
      paidAt: null, completedAt: null, refundedAt: null, canceledAt: null,
    },
    events: [event("cord_demo_20260916006", "status", "created", { createdAt: "2026-09-16T03:12:00.000Z", note: "创建订单" })],
  },
  {
    order: {
      id: "11111111-2222-4333-8444-555555555555", appId: "app_yahshi_web", appName: "Yahshi Web", appCode: "yahshi-web",
      userId: "11111111-1111-4111-8111-111111111111", userPublicId: "1001", userName: "林默",
      idempotencyKey: "idem_demo_virtual_projection", amountMinor: 100, currency: "CNY", title: "测试积分包",
      status: "paid", payload: { source: "wechat_virtual", channel: "wechat_virtual", purchaseId: "11111111-2222-4333-8444-555555555555", sandbox: true, pendingRefunds: 1, kind: "points" },
      paymentOrderId: null, version: 1,
      createdAt: "2026-09-23T14:46:00.000Z", updatedAt: "2026-09-23T14:47:00.000Z",
      paidAt: "2026-09-23T14:46:00.000Z", completedAt: null, refundedAt: null, canceledAt: null,
      source: "wechat_virtual", sandbox: true, purchaseId: "11111111-2222-4333-8444-555555555555",
      refundedAmountMinor: 40, virtualDelivered: true,
    },
    events: [event("11111111-2222-4333-8444-555555555555", "status", "paid", { fromStatus: "created", createdAt: "2026-09-23T14:46:00.000Z", note: "微信发货已确认" })],
  },
];

function matchesQuery(order: AdminCommerceOrderDetail["order"], query: ReturnType<typeof adminCommerceOrderListQuerySchema.parse>): boolean {
  const keyword = query.keyword.trim().toLowerCase();
  const haystack = `${order.id} ${order.title} ${order.userName} ${order.userPublicId} ${order.appName} ${order.appCode} ${order.paymentOrderId ?? ""}`.toLowerCase();
  if (keyword && !haystack.includes(keyword)) return false;
  if (query.appId && order.appId !== query.appId) return false;
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(order.createdAt));
  if (query.dateFrom && day < query.dateFrom) return false;
  if (query.dateTo && day > query.dateTo) return false;
  return true;
}

export function createDevelopmentCommerceOrderApi(options: {
  inbox?: { enqueue(input: { appId: string; appName: string; userId: string; eventKey: string; occurrenceKey: string; category: "transactional"; title: string; titleUg?: string; body: string; bodyUg?: string; createdAt?: string }): unknown };
  webhooks?: { enqueue(input: { appId: string; eventKey: "order.paid" | "order.refunded"; occurrenceKey: string; payload: Record<string, unknown> }): unknown };
  fulfillMembership?: (order: { id: string; appId: string; userId: string; payload: Record<string, unknown>; paidAt: string | null }) => Promise<void>;
} = {}): CommerceOrderApi {
  const records = structuredClone(seedOrders);
  const pending = new Map<string, Promise<unknown>>();
  async function serialize<T>(id: string, action: () => Promise<T>): Promise<T> {
    const result = (pending.get(id) ?? Promise.resolve()).catch(() => undefined).then(action);
    pending.set(id, result);
    try { return await result; } finally { if (pending.get(id) === result) pending.delete(id); }
  }
  const api: CommerceOrderApi = {
    async list(query = {}): Promise<AdminCommerceOrderListResult> {
      const parsed = adminCommerceOrderListQuerySchema.parse(query);
      const scoped = records.filter((item) => matchesQuery(item.order, parsed)).sort((a, b) => b.order.createdAt.localeCompare(a.order.createdAt) || b.order.id.localeCompare(a.order.id));
      const filtered = scoped.filter((item) => parsed.status === "all" || item.order.status === parsed.status);
      const offset = (parsed.page - 1) * parsed.pageSize;
      const count = (status: CommerceOrderStatus) => scoped.filter((item) => item.order.status === status).length;
      return {
        items: filtered.slice(offset, offset + parsed.pageSize).map((item) => {
          const { payload: _, ...order } = item.order;
          return order;
        }),
        total: filtered.length,
        summary: {
          total: scoped.length, created: count("created"), paid: count("paid"),
          completed: count("completed"), refunded: count("refunded"), canceled: count("canceled"),
        },
      };
    },
    async get(orderId) {
      const value = records.find((item) => item.order.id === orderId);
      if (!value) throw new M1ApiError("ORDER_NOT_FOUND", "订单不存在");
      return structuredClone(value);
    },
    async transition(orderId, input: TransitionCommerceOrderInput) {
      return serialize(orderId, async () => {
        const index = records.findIndex((item) => item.order.id === orderId);
        const current = records[index] ? structuredClone(records[index]) : undefined;
        if (!current) throw new M1ApiError("ORDER_NOT_FOUND", "订单不存在");
        if (current.order.source === "wechat_virtual") throw new M1ApiError("VIRTUAL_PAYMENT_REQUIRED", "微信虚拟支付请通过购买记录查单确认，不能手工结清或退款");
        if (current.order.version !== input.expectedVersion) throw new M1ApiError("ORDER_STALE", "订单已被更新，请刷新后重试");
        if (current.order.payload.channel === "wechat_virtual" && (input.status === "paid" || input.status === "refunded")) throw new M1ApiError("VIRTUAL_PAYMENT_REQUIRED", "微信虚拟支付请通过购买记录查单确认，不能手工结清或退款");
        if (!canTransitionCommerceOrder(current.order.status, input.status)) throw new M1ApiError("INVALID_ORDER_TRANSITION", "订单状态不允许此跃迁");
        const at = now();
        const previous = current.order.status;
        current.order = {
          ...current.order,
          status: input.status,
          version: current.order.version + 1,
          updatedAt: at,
          paidAt: input.status === "paid" ? current.order.paidAt ?? at : current.order.paidAt,
          completedAt: input.status === "completed" ? current.order.completedAt ?? at : current.order.completedAt,
          refundedAt: input.status === "refunded" ? current.order.refundedAt ?? at : current.order.refundedAt,
          canceledAt: input.status === "canceled" ? current.order.canceledAt ?? at : current.order.canceledAt,
        };
        if (input.status === "paid") {
          await options.fulfillMembership?.({
            id: current.order.id,
            appId: current.order.appId,
            userId: current.order.userId,
            payload: current.order.payload,
            paidAt: current.order.paidAt,
          });
        }
        current.events.push(event(orderId, "status", input.status, { fromStatus: previous, createdAt: at }));
        if (input.status === "paid" || input.status === "refunded") {
          const eventKey = input.status === "paid" ? "order.paid" : "order.refunded";
          current.events.push(event(orderId, "notification", input.status, {
            fromStatus: input.status, eventKey, channel: "in_app", note: "站内信已记录，未发送短信", createdAt: at,
          }));
          options.inbox?.enqueue({
            appId: current.order.appId, appName: current.order.appName, userId: current.order.userId,
            eventKey, occurrenceKey: orderId, category: "transactional",
            title: input.status === "paid" ? "订单支付状态已记录" : "订单退款状态已记录",
            titleUg: input.status === "paid" ? "پۇل تۆلەش ھالىتى خاتىرىلەندى" : "پۇل قايتۇرۇش ھالىتى خاتىرىلەندى",
            body: `订单 ${orderId} 业务状态已更新，不代表微信资金到账或退款完成。`,
            bodyUg: `زاكاز ${orderId} سودا ھالىتى يېڭىلاندى؛ ۋېيشىن پۇلىنىڭ كەلگەنلىكى ياكى قايتۇرۇلغانلىقىنى بىلدۈرمەيدۇ.`,
            createdAt: at,
          });
          options.webhooks?.enqueue({
            appId: current.order.appId,
            eventKey,
            occurrenceKey: orderId,
            payload: {
              event: eventKey,
              appId: current.order.appId,
              occurredAt: at,
              data: {
                orderId,
                userId: current.order.userId,
                amountMinor: current.order.amountMinor,
                currency: current.order.currency,
                status: current.order.status,
                title: current.order.title,
              },
            },
          });
        }
        records[index] = current;
        return structuredClone(current);
      });
    },
    async settleExternal(orderId, input: SettleExternalCommerceOrderInput) {
      const current = records.find((item) => item.order.id === orderId);
      if (!current) throw new M1ApiError("ORDER_NOT_FOUND", "订单不存在");
      if (current.order.source === "wechat_virtual") throw new M1ApiError("VIRTUAL_PAYMENT_REQUIRED", "微信虚拟支付请通过购买记录查单确认，不能手工结清或退款");
      if (current.order.version !== input.expectedVersion) throw new M1ApiError("ORDER_STALE", "订单已被更新，请刷新后重试");
      if (current.order.payload.channel === "wechat_virtual") throw new M1ApiError("VIRTUAL_PAYMENT_REQUIRED", "微信虚拟支付请通过购买记录查单确认，不能手工结清或退款");
      if (!isExternalCommerceSettlement(current.order.payload)) throw new M1ApiError("ORDER_NOT_EXTERNAL", "这笔订单不是外部结清业务订单");
      if (!canTransitionCommerceOrder(current.order.status, "paid")) throw new M1ApiError("INVALID_ORDER_TRANSITION", "订单状态不允许此跃迁");
      return api.transition(orderId, { status: "paid", expectedVersion: input.expectedVersion });
    },
    async updateTitleUg(orderId, input) {
      return serialize(orderId, async () => {
        const index = records.findIndex((item) => item.order.id === orderId);
        const current = records[index] ? structuredClone(records[index]) : undefined;
        if (!current) throw new M1ApiError("ORDER_NOT_FOUND", "订单不存在");
        if (current.order.source === "wechat_virtual") throw new M1ApiError("ORDER_NOT_FOUND", "虚拟购买的维语名称请在会员商品中填写");
        const parsed = updateCommerceOrderTitleUgInputSchema.parse(input);
        if (current.order.version !== parsed.expectedVersion) throw new M1ApiError("ORDER_STALE", "订单已被更新，请刷新后重试");
        current.order.titleUg = parsed.titleUg;
        current.order.version += 1;
        current.order.updatedAt = now();
        records[index] = current;
        return structuredClone(current);
      });
    },
  };
  return api;
}
